import React, { ChangeEvent } from 'react';
import { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import { DropDown, IconInfoCircular } from '@noths/polaris-client-ribbons-design-system';
import { priceFormatter } from '@noths/polaris-client-utils';

import { DELIVERY_AREA_ID_PREFIX } from 'src/components/organisms/DeliveryArea/components';
import { SelectCountryPayload } from 'src/redux/deliveryZoneOptions';
import { DeliveryOption, DeliveryOptionInfo } from 'src/types';
import { getFormattedDateString } from 'src/utils';
import {
  COUNTRY_SELECT_LABEL,
  DELIVERY_NOT_AVAILABLE_TO_COUNTRY_FIELD_ERROR,
  DELIVERY_NOT_AVAILABLE_TO_COUNTRY_MESSAGE,
  FREE_DELIVERY,
  SELECTED_COUNTRIES_DELIVERY_ONLY,
  UK_DELIVERY_ONLY,
} from '../constants';
import { GroupedCountries } from '../modules';
import * as styles from '../styles';
import { DeliveryOptionSkeleton } from './DeliveryOptionSkeleton';
import DeliveryProviderLogo from './DeliveryProviderLogo';

export interface DeliveryBlockProps {
  className?: string;
  groupedCountries: GroupedCountries;
  deliveryOptions: DeliveryOptionInfo[];
  deliveryOptionsPlaceholderItems: DeliveryOption[];
  hasSelectedCountriesDeliveryOnly: boolean;
  hasUkDeliveryOnly: boolean;
  idPrefix?: string;
  selectedCountry: string;
  isLoading: boolean;
  onCountryChange: (payload: SelectCountryPayload) => void;
  displayDeliveryProviders: boolean;
}

export const DeliveryBlock = ({
  className,
  groupedCountries,
  deliveryOptions,
  deliveryOptionsPlaceholderItems,
  hasSelectedCountriesDeliveryOnly,
  hasUkDeliveryOnly,
  idPrefix = '',
  selectedCountry,
  onCountryChange,
  isLoading,
  displayDeliveryProviders,
}: DeliveryBlockProps) => {
  const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const [countryCode, deliveryZone] = e.target.value.split('|') as [string, DeliveryZoneCode];
    const sectionParent: SelectCountryPayload['sectionParent'] = idPrefix.includes(
      DELIVERY_AREA_ID_PREFIX,
    )
      ? 'delivery-area'
      : 'delivery-summary';

    onCountryChange({ selectedCountry: countryCode, selectedZone: deliveryZone, sectionParent });
  };

  const isDeliveryAvailable = Boolean(deliveryOptionsPlaceholderItems.length);

  return (
    <div css={styles.deliveryBlock} className={className}>
      <DropDown
        css={styles.countrySelect}
        options={groupedCountries}
        label={COUNTRY_SELECT_LABEL}
        id={`${idPrefix}delivery-block-countries`}
        onChange={handleCountryChange}
        value={selectedCountry}
        disabled={isLoading}
        error={isDeliveryAvailable ? null : DELIVERY_NOT_AVAILABLE_TO_COUNTRY_FIELD_ERROR}
      />

      {isDeliveryAvailable ? (
        <>
          {hasUkDeliveryOnly && (
            <div css={styles.countryInfoWrapper}>
              <IconInfoCircular width={16} height={16} />
              <p css={styles.countryInfoMessage}>{UK_DELIVERY_ONLY}</p>
            </div>
          )}
          {hasSelectedCountriesDeliveryOnly && (
            <div css={styles.countryInfoWrapper}>
              <IconInfoCircular width={16} height={16} />
              <p css={styles.countryInfoMessage}>{SELECTED_COUNTRIES_DELIVERY_ONLY}</p>
            </div>
          )}
          {isLoading ? (
            <div css={styles.deliveryOptionsList}>
              {deliveryOptionsPlaceholderItems.map(({ service: { code } }, i) => (
                <DeliveryOptionSkeleton isFirstOption={i === 0} key={`skeleton-${code}`} />
              ))}
            </div>
          ) : (
            <ul css={[styles.deliveryOptionsList, styles.fadeIn]}>
              {deliveryOptions.map(
                ({ date, exactDate, optionCode, min, max, name, price, deliveryProvider }, i) => (
                  <li css={styles.deliveryOption} key={optionCode}>
                    <div css={styles.deliveryOptionWrapper}>
                      {displayDeliveryProviders && deliveryProvider?.name && (
                        <DeliveryProviderLogo name={deliveryProvider.name} />
                      )}
                      <div css={styles.dateAndPriceWrapper}>
                        <span>
                          <p css={styles.deliveryType}>{name}</p>
                          <p css={[styles.dateAndPrice, i === 0 && styles.firstDateAndPrice]}>
                            {exactDate && date
                              ? getFormattedDateString(date)
                              : `${min} - ${max} days from dispatch`}
                          </p>
                        </span>
                        <p css={[styles.dateAndPrice, i === 0 && styles.firstDateAndPrice]}>
                          {Number(price.amount) === 0
                            ? FREE_DELIVERY
                            : priceFormatter(price.currencyCode, Number(price.amount), false)}
                        </p>
                      </div>
                    </div>
                  </li>
                ),
              )}
            </ul>
          )}
        </>
      ) : (
        <p css={styles.deliveryNotAvailableMessage}>{DELIVERY_NOT_AVAILABLE_TO_COUNTRY_MESSAGE}</p>
      )}
    </div>
  );
};
